/**
 * /dashboard/hiring/candidates/$id — Candidate profile (Phase H.8).
 *
 * HR-admin OR an interviewer assigned to one of the candidate's
 * applications (server-resolved). A forbidden/unknown id is a NEUTRAL
 * 404 (no existence oracle).
 *
 * The application context is carried in ?application=<id> when you
 * arrive from the pipeline board.
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/hiring/candidates/[id]/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - useSearchParams (next) → Route.useSearch()
 *   - next/link Link → @tanstack/react-router Link
 */

import { useState } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { z } from "zod";
import {
  ArrowLeft,
  Download,
  FileText,
  GitBranch,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useCandidate } from "@/hooks/use-hiring";
import { toApiError, hiringApi } from "@/lib/api";
import { useDirectory } from "@/hooks/use-directory";
import {
  CANDIDATE_SOURCE_LABELS,
  fmtDate,
} from "@/lib/hiring-format";
import { HiringModuleShell } from "@/components/shared/hiring-module-shell";
import { OfferPanel } from "@/components/hiring/offer-panel";
import { HireDialog } from "@/components/hiring/hire-dialog";
import { ScheduleInterviewDialog } from "@/components/hiring/schedule-interview-dialog";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

const candidateSearchSchema = z.object({
  // ApplicationCard passes the numeric application id from the pipeline
  // board. We accept either number or numeric string for robustness.
  application: z.coerce.number().int().positive().optional(),
});

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/hiring/candidates/$id",
)({
  validateSearch: candidateSearchSchema,
  component: CandidateProfileRoute,
});

function ResumeButton({ candidateId }: { candidateId: number }) {
  const [busy, setBusy] = useState(false);
  async function onView() {
    setBusy(true);
    try {
      const blob = await hiringApi.downloadResume(candidateId);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(toApiError(e).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Button
      variant="outline"
      onClick={onView}
      disabled={busy}
      className="gap-1.5"
    >
      {busy ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Download className="h-4 w-4" />
      )}
      View résumé
    </Button>
  );
}

function CandidateProfile({
  id,
  applicationId,
}: {
  id: number;
  applicationId: number | null;
}) {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const candQ = useCandidate(id);
  const dir = useDirectory();

  if (candQ.isLoading) {
    return (
      <HiringModuleShell title="Candidate">
        <Card>
          <CardContent className="space-y-4 py-8">
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </HiringModuleShell>
    );
  }

  if (candQ.isError) {
    const e = toApiError(candQ.error);
    return (
      <HiringModuleShell title="Candidate">
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-muted-foreground">
              {e.error === "not_found" || e.error === "forbidden"
                ? "This candidate isn't available. You need to be an HR admin or an assigned interviewer for them."
                : e.message}
            </p>
            <Button asChild variant="outline" className="gap-1.5">
              <Link to="/app/$tenantSlug/hiring/candidates" params={{ tenantSlug }}>
                <ArrowLeft className="h-4 w-4" />
                Back to Candidates
              </Link>
            </Button>
          </CardContent>
        </Card>
      </HiringModuleShell>
    );
  }

  const c = candQ.data!.candidate;
  const fullName =
    `${c.first_name} ${c.last_name}`.trim() || c.email;

  return (
    <HiringModuleShell
      title={fullName}
      description={c.email}
      actions={
        <div className="flex flex-wrap gap-2">
          {applicationId != null && (
            <ScheduleInterviewDialog
              applicationId={applicationId}
            />
          )}
          <Button asChild variant="ghost" className="gap-1.5">
            <Link to="/app/$tenantSlug/hiring/candidates" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              All candidates
            </Link>
          </Button>
        </div>
      }
    >
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">Email</dt>
                <dd className="font-medium">{c.email}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Phone</dt>
                <dd className="font-medium">
                  {c.phone ?? "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Source</dt>
                <dd>
                  <StatusBadge
                    label={CANDIDATE_SOURCE_LABELS[c.source]}
                    tone="info"
                  />
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">
                  Consent
                </dt>
                <dd>
                  {c.consent_given ? (
                    <StatusBadge label="Given" tone="success" />
                  ) : (
                    <StatusBadge
                      label="Not given"
                      tone="warning"
                    />
                  )}
                </dd>
              </div>
              {c.source_url && (
                <div className="sm:col-span-2">
                  <dt className="text-muted-foreground">
                    Source link
                  </dt>
                  <dd className="break-all font-medium">
                    <a
                      href={c.source_url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-primary hover:underline"
                    >
                      {c.source_url}
                    </a>
                  </dd>
                </div>
              )}
              {c.retention_until && (
                <div>
                  <dt className="text-muted-foreground">
                    Retention until
                  </dt>
                  <dd className="font-medium">
                    {fmtDate(c.retention_until)}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-muted-foreground">Added</dt>
                <dd className="font-medium">
                  {fmtDate(c.created_at)}
                </dd>
              </div>
              {c.imported_by_id != null && (
                <div>
                  <dt className="text-muted-foreground">
                    Imported by
                  </dt>
                  <dd className="font-medium">
                    {dir.label(c.imported_by_id)}
                  </dd>
                </div>
              )}
              {c.referred_by_id != null && (
                <div>
                  <dt className="text-muted-foreground">
                    Referred by
                  </dt>
                  <dd className="font-medium">
                    {dir.label(c.referred_by_id)}
                  </dd>
                </div>
              )}
            </dl>

            {c.notes && (
              <div className="rounded-md bg-muted/50 p-3 text-sm">
                <p className="mb-1 font-medium text-gray-900">
                  Notes
                </p>
                <p className="whitespace-pre-wrap text-muted-foreground">
                  {c.notes}
                </p>
              </div>
            )}

            <div className="flex items-center gap-3 border-t pt-4">
              {c.has_resume ? (
                <ResumeButton candidateId={c.id} />
              ) : (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  No résumé on file.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Hire</CardTitle>
            <CardDescription>
              Turn this candidate into an employee and start their
              onboarding.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {applicationId != null ? (
              <>
                <HireDialog
                  applicationId={applicationId}
                  candidate={c}
                />
                <p className="text-xs text-muted-foreground">
                  Only an active application can be hired. The new hire
                  gets a one-time password (no invite email).
                </p>
              </>
            ) : (
              <Alert>
                <AlertTitle>Open from the pipeline</AlertTitle>
                <AlertDescription className="space-y-2">
                  <p>
                    To hire, schedule an interview or make an offer,
                    open this candidate from a requisition&apos;s
                    pipeline board (that&apos;s where their application
                    lives).
                  </p>
                  <Button
                    asChild
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                  >
                    <Link to="/app/$tenantSlug/hiring/requisitions" params={{ tenantSlug }}>
                      <GitBranch className="h-4 w-4" />
                      Go to requisitions
                    </Link>
                  </Button>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>

      {applicationId != null && (
        <div className="mt-6">
          <OfferPanel applicationId={applicationId} />
        </div>
      )}
    </HiringModuleShell>
  );
}

function CandidateProfileRoute() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const numId = Number(id);
  const applicationId = search.application ?? null;

  if (Number.isFinite(numId) && numId > 0) {
    return (
      <CandidateProfile
        id={numId}
        applicationId={applicationId}
      />
    );
  }
  return (
    <HiringModuleShell title="Candidate">
      <Card>
        <CardContent className="py-16 text-center">
          <p className="text-sm text-muted-foreground">
            Invalid candidate.
          </p>
        </CardContent>
      </Card>
    </HiringModuleShell>
  );
}
